import React from 'react';

// Presentational primitives shared by the Workspace and Teams views of the
// Settings → Team page, so the two tabs read as one surface. Table chrome
// mirrors WorkflowsTable / RunHistoryTable; everything is drawn from the
// global.css tokens (which the main bundle loads app-wide).
//
// The one deliberate accent on these pages is the access ramp: a grant that
// covers the whole account is a filled navy chip, an individual workspace grant
// is a quiet outlined one. Weight encodes privilege.

export const ts = {
  page: { padding: '24px 24px 28px' },

  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap', marginBottom: '20px' },
  // A header that carries more than one control (search + primary action).
  headerActions: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
  search: {
    height: '36px', width: '220px', maxWidth: '100%', padding: '0 12px',
    borderRadius: 'var(--radius, 6px)', border: '1px solid var(--border, #e2e8f0)',
    background: 'var(--surface, #fff)', fontSize: '13px',
    color: 'var(--text-primary, #0f172a)', outline: 'none',
  },
  // Full-width "nothing matched" row, so a filtered table keeps its header and
  // its search box instead of collapsing into the page-level empty state.
  noMatch: { padding: '28px 16px', textAlign: 'center', fontSize: '13px', color: 'var(--text-secondary, #64748b)' },
  title: { margin: 0, fontSize: '18px', fontWeight: 700, color: 'var(--text-primary, #0f172a)', letterSpacing: '-0.01em' },
  subtitle: { margin: '6px 0 0', fontSize: '13px', lineHeight: 1.65, color: 'var(--text-secondary, #64748b)', maxWidth: '640px' },

  surface: {
    border: '1px solid var(--border, #e2e8f0)', borderRadius: 'var(--radius-lg, 12px)',
    background: 'var(--surface, #fff)', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)', overflow: 'hidden',
  },
  scroll: { overflowX: 'auto' },
  // minWidth makes the wrapper above scroll on a narrow panel instead of letting
  // the browser crush "Acme Wholesale (987-654-3210)" into a four-line tower.
  table: { width: '100%', minWidth: '880px', borderCollapse: 'collapse' },
  th: {
    padding: '11px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600,
    color: 'var(--text-secondary, #64748b)', background: '#f8fafc',
    borderBottom: '1px solid var(--border, #e2e8f0)',
    textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
  },
  td: (last) => ({
    padding: '12px 16px', fontSize: '13px', color: 'var(--text-primary, #0f172a)',
    borderBottom: last ? 'none' : '1px solid var(--border, #e2e8f0)', verticalAlign: 'middle',
  }),
  row: { transition: 'background 0.1s' },
  actionsCell: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '2px' },

  sectionLabel: {
    display: 'flex', alignItems: 'center', gap: '8px',
    margin: '26px 0 10px', fontSize: '12px', fontWeight: 600,
    color: 'var(--text-secondary, #64748b)', textTransform: 'uppercase', letterSpacing: '0.05em',
  },

  nameCell: { display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 },
  // Emails and workspace names never break mid-word — the surface scrolls instead.
  name: { fontWeight: 500, color: 'var(--text-primary, #0f172a)', whiteSpace: 'nowrap' },
  // Row actions sit on a wide row on their own, so they need more presence than
  // the tight icon clusters elsewhere in the dashboard.
  rowIcon: { padding: '4px' },
  muted: { color: 'var(--text-muted, #94a3b8)' },
  sub: { fontSize: '11px', color: 'var(--text-muted, #94a3b8)', marginTop: '2px' },
  link: { color: 'var(--primary, #2563eb)', textDecoration: 'none', whiteSpace: 'nowrap' },

  // Three tiers, three weights — the same "weight encodes privilege" rule the
  // access chips follow.
  rolePill: (role) => ({
    display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: '99px',
    fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap',
    ...(role === 'owner'
      ? { color: 'var(--primary, #132848)', background: 'rgba(19, 40, 72, 0.08)' }
      : role === 'admin'
        ? { color: '#2563eb', background: '#eff6ff' }
        : { color: 'var(--text-secondary, #64748b)', background: '#f1f5f9' }),
  }),

  chipRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' },
  chipStrong: {
    display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 'var(--radius, 6px)',
    fontSize: '12px', fontWeight: 500, color: '#fff', background: 'var(--primary, #132848)', whiteSpace: 'nowrap',
  },
  chip: {
    display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 'var(--radius, 6px)',
    fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary, #64748b)',
    background: '#f8fafc', border: '1px solid var(--border, #e2e8f0)', whiteSpace: 'nowrap',
  },
  chipMore: {
    display: 'inline-flex', alignItems: 'center', padding: '3px 8px', borderRadius: 'var(--radius, 6px)',
    fontSize: '12px', fontWeight: 500, color: 'var(--text-muted, #94a3b8)', cursor: 'help', whiteSpace: 'nowrap',
  },

  empty: { padding: '52px 24px', textAlign: 'center' },
  emptyText: { margin: '0 auto 16px', maxWidth: '420px', fontSize: '13px', lineHeight: 1.65, color: 'var(--text-secondary, #64748b)' },

  notice: { padding: '48px 24px', textAlign: 'center', fontSize: '14px', color: 'var(--text-secondary, #64748b)' },
};

// Stroke plus for the primary actions — a text "+" renders lighter than the
// 13/600 label next to it.
export function PlusIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M7 2.5v9M2.5 7h9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div style={ts.header}>
      <div style={{ minWidth: 0 }}>
        <h2 style={ts.title}>{title}</h2>
        {subtitle && <p style={ts.subtitle}>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

// Initials from an email local-part, so a column of similar-looking addresses
// stays scannable without leaning on colour. People only — workspaces share
// prefixes ("TE Workspace …") often enough that a monogram would collide and
// tell you nothing the name beside it doesn't.
export function initialsOf(value) {
  const base = String(value || '').split('@')[0].trim();
  const parts = base.split(/[\s._\-/]+/).filter(Boolean);
  const raw = parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}` : base.slice(0, 2);
  return (raw || '?').toUpperCase().slice(0, 2);
}

export function Monogram({ label, size = 28 }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: `${size}px`, height: `${size}px`, flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: 'var(--radius, 6px)', background: '#f1f5f9',
        border: '1px solid var(--border, #e2e8f0)',
        color: '#475569', fontSize: '11px', fontWeight: 600, letterSpacing: '0.02em',
      }}
    >
      {initialsOf(label)}
    </span>
  );
}

// One tier per line, so the pill can be read without a legend. Platform staff
// have no entry here on purpose: they are not an account role and the Teams page
// doesn't list them.
const ROLE_LABEL = { owner: 'Owner', admin: 'Admin', member: 'Member' };
const ROLE_TITLE = {
  owner: 'Owns the account — full access, billing, and can transfer ownership',
  admin: 'Manages the team and every workspace. No billing',
  member: 'Uses the app in the workspaces they have been granted',
};

export function RolePill({ role }) {
  const key = ROLE_LABEL[role] ? role : 'member';
  return <span style={ts.rolePill(key)} title={ROLE_TITLE[key]}>{ROLE_LABEL[key]}</span>;
}

/** Marks the signed-in user's own row, so "can I remove myself?" is obvious. */
export function SelfTag() {
  return <span style={{ ...ts.chip, color: 'var(--text-muted, #94a3b8)' }}>You</span>;
}

// `nameOf` resolves a workspace id to its display name. Grants beyond `max`
// collapse into a "+N more" chip so one over-provisioned member can't stretch
// the row.
export function AccessChips({ fullAccess = false, accountLevel = false, workspaceIds = [], nameOf, max = 3 }) {
  if (fullAccess) return <span style={ts.chipStrong}>Full access</span>;

  const names = workspaceIds.map((id) => nameOf(id)).filter(Boolean);
  if (!accountLevel && !names.length) return <span style={ts.muted}>No access yet</span>;

  const shown = names.slice(0, max);
  const rest = names.slice(max);
  return (
    <span style={ts.chipRow}>
      {accountLevel && <span style={ts.chipStrong}>Account level</span>}
      {shown.map((n, i) => <span key={`${n}-${i}`} style={ts.chip}>{n}</span>)}
      {rest.length > 0 && <span style={ts.chipMore} title={rest.join(', ')}>+{rest.length} more</span>}
    </span>
  );
}

export function EmptyState({ message, action }) {
  return (
    <div style={ts.empty}>
      <p style={ts.emptyText}>{message}</p>
      {action}
    </div>
  );
}

// Static grey bars while /team is in flight — calmer than a "Loading…" string
// and it holds the table's shape so the page doesn't jump.
export function SkeletonRows({ columns, rows = 3 }) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r}>
          {Array.from({ length: columns }, (__, c) => (
            <td key={c} style={ts.td(r === rows - 1)}>
              <span style={{ display: 'block', height: '10px', borderRadius: '99px', background: '#f1f5f9', width: c === 0 ? '60%' : '40%' }} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export const hoverRow = {
  onMouseEnter: (e) => { e.currentTarget.style.background = '#f8fafc'; },
  onMouseLeave: (e) => { e.currentTarget.style.background = 'transparent'; },
};
